/**
 * Dev log display and client-side filtering helpers.
 */
import type { SessionLogEntry } from "@memes/shared";

export function isErrorLevel(level: string): boolean {
  return level === "error" || level === "fatal";
}

export function formatLogSummary(entry: SessionLogEntry): string {
  const method = entry.method as string | undefined;
  const url = entry.url as string | undefined;
  const statusCode = entry.statusCode as number | undefined;
  const responseTime = entry.responseTime as number | undefined;

  if (method && url) {
    const base = `${method} ${url}`;
    if (statusCode != null) {
      const rt = responseTime != null ? ` (${responseTime}ms)` : "";
      return `${base} → ${statusCode}${rt}`;
    }
    return base;
  }

  if (entry.msg === "incoming request" || entry.msg === "request completed") {
    if (statusCode != null) {
      const rt = responseTime != null ? ` (${responseTime}ms)` : "";
      return `${entry.msg} → ${statusCode}${rt}`;
    }
  }

  return entry.msg || "(no message)";
}

export interface LogFilterOpts {
  q?: string;
  errorsOnly?: boolean;
}

/** Lines shown above/below a pinned selection in the live log viewer. */
export const LIVE_LOG_CONTEXT_BEFORE = 40;
export const LIVE_LOG_CONTEXT_AFTER = 40;

export interface PinnedLogContext {
  /** Frozen slice shown while pinned. */
  lines: SessionLogEntry[];
  /** Index within `lines` of the selected entry. */
  centerIndex: number;
  /** Total buffered line count when the user pinned. */
  bufferedAtPin: number;
}

export function findLogEntryIndex(
  entries: SessionLogEntry[],
  needle: SessionLogEntry,
): number {
  return entries.findIndex(
    (e) =>
      e.ts === needle.ts &&
      e.level === needle.level &&
      e.msg === needle.msg &&
      e.source === needle.source &&
      e.module === needle.module,
  );
}

export function sliceLogContext(
  entries: SessionLogEntry[],
  centerIndex: number,
  before = LIVE_LOG_CONTEXT_BEFORE,
  after = LIVE_LOG_CONTEXT_AFTER,
): PinnedLogContext {
  const start = Math.max(0, centerIndex - before);
  const end = Math.min(entries.length, centerIndex + after + 1);
  return {
    lines: entries.slice(start, end),
    centerIndex: centerIndex - start,
    bufferedAtPin: entries.length,
  };
}

export function filterLogEntries(
  entries: SessionLogEntry[],
  opts: LogFilterOpts,
): SessionLogEntry[] {
  let result = entries;
  if (opts.errorsOnly) {
    result = result.filter((e) => isErrorLevel(e.level));
  }
  const q = opts.q?.trim().toLowerCase();
  if (q) {
    result = result.filter((e) => JSON.stringify(e).toLowerCase().includes(q));
  }
  return result;
}
